import { useEffect, type ReactNode } from 'react';
import {
	Briefcase,
	CalendarDays,
	Clock3,
	Fingerprint,
	IdCard,
	Mail,
	Phone,
	User,
	Video,
	type LucideIcon,
} from 'lucide-react';
import { useModal } from '@/hooks/useModal';
import { PATIENT_GENDER_LABELS } from '@/constants/patient';
import {
	formatBirthWithAgeDisplay,
	formatCpfDisplay,
	formatPhoneDisplay,
} from '@/utils/participantDetailDisplay';
import { cn } from '@/lib/utils';
import { PatientWhatsAppSchedulingNotice } from '@/components/whatsapp/PatientWhatsAppSchedulingNotice';
import { isPatientWhatsAppNotificationsActive } from '@/utils/patientWhatsAppNotifications';

interface ProfessionalConfirmationModalContentProps {
	patient: Patients.Patient;
	scheduleData: {
		date: string;
		time: string;
	};
	selectedModality: Professionals.ServiceModality;
	mode?: 'create' | 'edit';
	modalitySummaryOverride?: string;
	onAlterWhatsApp?: () => void;
}

const MODALITY_SUMMARY_LABEL: Record<Professionals.ServiceModality, string> = {
	FULL_PRESENCIAL: 'Presencial',
	FULL_REMOTE: 'Online',
	HYBRID: 'Híbrido',
};

const EMPTY_FIELD = '—';

const SummaryCard = ({
	children,
	className,
}: {
	children: ReactNode;
	className?: string;
}) => (
	<div
		className={cn(
			'rounded-2xl border border-gray-100 bg-gray-50 p-5',
			className,
		)}
	>
		{children}
	</div>
);

const SummaryField = ({
	icon: Icon,
	label,
	value,
}: {
	icon: LucideIcon;
	label: string;
	value: string;
}) => (
	<>
		<div className="mb-3 flex items-center gap-2 text-gray-400">
			<Icon size={16} />
			<span className="text-[10px] font-bold uppercase tracking-wider">
				{label}
			</span>
		</div>
		<p className="text-sm font-bold text-gray-900">{value}</p>
	</>
);

const VerifyField = ({
	icon: Icon,
	label,
	value,
	fullWidth = false,
}: {
	icon: LucideIcon;
	label: string;
	value: string | null | undefined;
	fullWidth?: boolean;
}) => (
	<div className={cn('min-w-0', fullWidth && 'sm:col-span-2')}>
		<div className="mb-1.5 flex items-center gap-1.5 text-gray-400">
			<Icon className="size-3.5 shrink-0" strokeWidth={2.25} />
			<span className="text-[10px] font-bold uppercase tracking-wider">
				{label}
			</span>
		</div>
		<p className="break-words text-sm font-semibold leading-snug text-gray-900">
			{value?.trim() ? value : EMPTY_FIELD}
		</p>
	</div>
);

const ProfessionalConfirmationModalContent = ({
	patient,
	scheduleData,
	selectedModality,
	mode = 'create',
	modalitySummaryOverride,
	onAlterWhatsApp,
}: ProfessionalConfirmationModalContentProps) => {
	const { setConfirmText } = useModal();

	useEffect(() => {
		setConfirmText(mode === 'edit' ? 'Salvar alterações' : 'Agendar');
	}, [setConfirmText, mode]);

	const cpfLabel = formatCpfDisplay(patient.cpf);
	const birthLabel = formatBirthWithAgeDisplay(patient.birth_date);
	const genderLabel = patient.gender
		? PATIENT_GENDER_LABELS[patient.gender]
		: null;
	const phoneLabel = formatPhoneDisplay(patient.phone);
	const showWhatsAppNotice = isPatientWhatsAppNotificationsActive(patient);

	return (
		<section className="flex w-full justify-center">
			<div className="w-full max-w-2xl space-y-4 rounded-[2.5rem] p-6 md:space-y-5">
				<div className="flex w-full items-start gap-5 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
					<div className="flex h-[4.5rem] w-[4.5rem] shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-purple to-blue-500 text-white shadow-md shadow-purple-200/60">
						<User className="size-8" strokeWidth={2} />
					</div>
					<div className="min-w-0 flex-1 pt-0.5">
						<p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-emerald-600">
							Paciente da consulta
						</p>
						<h3 className="mb-1 text-xl font-bold leading-tight text-gray-900 md:text-2xl">
							{patient.full_name}
						</h3>
						<p className="text-xs text-gray-500">
							{mode === 'edit'
								? 'Confira os dados abaixo antes de salvar as alterações.'
								: 'Confira os dados abaixo antes de confirmar o agendamento.'}
						</p>
					</div>
				</div>

				<SummaryCard className="bg-white">
					<p className="mb-4 text-[10px] font-bold uppercase tracking-wider text-gray-400">
						Dados para conferência
					</p>
					<div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
						<VerifyField
							icon={Fingerprint}
							label="CPF"
							value={cpfLabel}
						/>
						<VerifyField
							icon={IdCard}
							label="RG"
							value={patient.rg}
						/>
						<VerifyField
							icon={CalendarDays}
							label="Data de nascimento"
							value={birthLabel}
						/>
						<VerifyField
							icon={User}
							label="Gênero"
							value={genderLabel}
						/>
						<VerifyField
							icon={Phone}
							label="Telefone"
							value={phoneLabel}
						/>
						<VerifyField
							icon={Mail}
							label="E-mail"
							value={patient.email}
						/>
						<VerifyField
							icon={Briefcase}
							label="Profissão"
							value={patient.profession}
							fullWidth
						/>
						{patient.guardian_full_name?.trim() && (
							<VerifyField
								icon={User}
								label="Responsável legal"
								value={patient.guardian_full_name}
								fullWidth
							/>
						)}
					</div>
				</SummaryCard>

				{showWhatsAppNotice ? (
					<PatientWhatsAppSchedulingNotice
						onAlter={onAlterWhatsApp}
					/>
				) : null}

				<div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-3">
					<SummaryCard>
						<SummaryField
							icon={CalendarDays}
							label="Data"
							value={scheduleData.date}
						/>
					</SummaryCard>
					<SummaryCard>
						<SummaryField
							icon={Clock3}
							label="Horário"
							value={scheduleData.time}
						/>
					</SummaryCard>
					<SummaryCard>
						<SummaryField
							icon={Video}
							label="Modalidade"
							value={
								modalitySummaryOverride ??
								MODALITY_SUMMARY_LABEL[selectedModality]
							}
						/>
					</SummaryCard>
				</div>
			</div>
		</section>
	);
};

export { ProfessionalConfirmationModalContent };
